import { Card } from '@repo/ui/Card';

import { gql } from '@apollo/client';

import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';

import { Form, TextInputField, RadioInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';

import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { useFieldArray } from 'react-hook-form';
import { Mercury } from '@repo/types/Models';

const whitelistSchema = z.object({
  address: z.union([z.ipv4(), z.ipv6()], { error: 'Invalid IP address.' }),
  name: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
  scope: z.enum(['API', 'GQL', 'ANY']),
});

const whitelistFormSchema = z.object({
  items: z.array(whitelistSchema),
});

type WhitelistFormParams = TypeOf<typeof whitelistFormSchema>;

interface WhitelistFormProps {
  accountId: Mercury.Models.Account['id'];
  refetch: () => void;
}

const WhitelistForm = ({ accountId, refetch }: WhitelistFormProps) => {
  // Mutation
  const BULK_CREATE_ACCOUNT_ALLOWED_ACCESS_IPS = gql`
    mutation BulkCreateAllowedAccessIpsForAccount(
      $account_id: ID!
      $input: BulkCreateAllowedAccessIpsForAccountInput!
    ) {
      bulk_create_allowed_access_ips_for_account(account_id: $account_id, input: $input) {
        items {
          id
        }
      }
    }
  `;

  const client = useApolloClient();

  const onSubmit = async ({ ...input }: WhitelistFormParams) => {
    await client.mutate({
      mutation: BULK_CREATE_ACCOUNT_ALLOWED_ACCESS_IPS,
      variables: {
        account_id: accountId,
        input,
      },
    });
    form.reset();
    refetch();
  };
  const form = useZodForm({
    schema: whitelistFormSchema,
    onSubmit,
    defaultValues: {
      items: [{ address: '', name: '', scope: 'API' }],
    },
  });

  const {
    fields: itemsFields,
    append: appendItemField,
    remove: removeItemField,
  } = useFieldArray({
    name: 'items',
    control: form.control,
  });

  return (
    <Card title="Add IP to whitelist">
      <Form {...form} column={1} className="!max-w-none">
        <Form.Success>Account IP Whitelist created.</Form.Success>
        <Form.Error>
          Unable to create account IP Whitelist. Please try again later or contact support if error
          persists.
        </Form.Error>

        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4 pb-4 border-b border-grey-400">
            <div className="flex flex-col gap-4">
              {itemsFields.map((field, index) => (
                <div key={field.id} className="flex items-center">
                  <div className="flex gap-8 max-w-3xl">
                    <TextInputField
                      label="Address"
                      placeholder="IP Address"
                      additionalContainerClasses="w-full max-w-[220px]"
                      {...registerInput(form, `items.${index}.address`)}
                    />

                    <TextInputField
                      label="Name"
                      placeholder="Name"
                      additionalContainerClasses="w-full max-w-[200px]"
                      {...registerInput(form, `items.${index}.name`)}
                    />

                    <div className="flex flex-col gap-3">
                      <label className="text-sm font-bold" htmlFor={`items.${index}.scope`}>
                        Scope
                      </label>
                      <div className="flex gap-4 items-center mt-2 sm:col-span-2">
                        <RadioInputField
                          label="API"
                          {...registerInput(form, `items.${index}.scope`)}
                          value="API"
                        />
                        <RadioInputField
                          label="Dashboard"
                          {...registerInput(form, `items.${index}.scope`)}
                          value="GQL"
                        />
                        <RadioInputField
                          label="Any"
                          {...registerInput(form, `items.${index}.scope`)}
                          value="ANY"
                        />

                        {itemsFields.length > 1 && index > 0 && (
                          <Button
                            variant="danger-secondary"
                            size="sm"
                            className="max-h-[42px]"
                            onClick={() => removeItemField(index)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="secondary"
              icon="add"
              size="sm"
              onClick={() => appendItemField({ address: '', name: '', scope: 'API' })}
              className="max-w-fit"
            >
              Add IP Address
            </Button>
          </div>

          <Button type="submit" className="max-w-fit">
            Submit
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default WhitelistForm;
